import { Injectable } from '@angular/core';
import {
    EventType,
    Navigation,
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    NavigationSkipped,
    NavigationStart,
    Router,
    Event as RouterEvent,
} from '@angular/router';
import { EventTrackerEvents, EventTrackerEventStatuses } from '@shared/constants';
import { EventTrackerEventStatus } from '@shared/types';
import { EventTrackerEventDto } from 'src/.api-client/models/event-tracker-event-dto';
import { v4 as uuidv4 } from 'uuid';
import { EventTrackerService } from './event-tracker.service';

type NavigationInfo = {
    traceId: string;
    url: string;
    from: string;
    trigger?: NavigationStart['navigationTrigger'];
    urlAfterRedirects?: string;
};

type AdditionalNavigationEventData = {
    from: string;
    toRequested: string;
    toResolved: string | undefined;
    navigatedBy: string | undefined;
    trigger: NavigationStart['navigationTrigger'];
};

type SuccessOrErrorEventStatus = typeof EventTrackerEventStatuses.Success | typeof EventTrackerEventStatuses.Error;

@Injectable({ providedIn: 'root' })
export class NavigationEventTrackingService {
    private _navigationInfos = new Map<number, NavigationInfo>();

    constructor(
        private _router: Router,
        private _eventTrackerService: EventTrackerService,
    ) {
        this._router.events.subscribe((event: RouterEvent) => {
            this._onRouterEvent(event);
        });
    }

    private _onRouterEvent(event: RouterEvent): void {
        switch (event.type) {
            case EventType.NavigationStart:
                return this._onNavigationStart(event);
            case EventType.NavigationEnd:
                return this._onNavigationEnd(event);
            case EventType.NavigationSkipped:
                return this._onNavigationSkipped(event);
            case EventType.NavigationCancel:
                return this._onNavigationCancel(event);
            case EventType.NavigationError:
                return this._onNavigationError(event);
            default:
                break;
        }
    }

    private _onNavigationStart(event: NavigationStart): void {
        // console.log('NavigationStart', event);

        this._navigationInfos.set(event.id, {
            traceId: uuidv4(),
            url: event.url,
            from: this._router.url,
            trigger: event.navigationTrigger,
        });
    }

    private _onNavigationEnd(event: NavigationEnd): void {
        // console.log('NavigatonEnd', event);
        this._endNavigationInfos(event.id, EventTrackerEventStatuses.Success, event.urlAfterRedirects);
    }

    private _onNavigationCancel(event: NavigationCancel): void {
        // console.log('NavigationCancel', event);
        this._endNavigationInfos(event.id, EventTrackerEventStatuses.Success, undefined);
    }

    private _onNavigationSkipped(event: NavigationSkipped): void {
        // console.log('NavigationSkipped', event);
        this._endNavigationInfos(event.id, EventTrackerEventStatuses.Success, undefined);
    }

    private _onNavigationError(event: NavigationError): void {
        // console.log('NavigationError', event);
        this._endNavigationInfos(event.id, EventTrackerEventStatuses.Error, undefined);
    }

    private _endNavigationInfos(id: number, eventStatus: SuccessOrErrorEventStatus, urlAfterRedirects: string | undefined): void {
        const navigationInfo = this._navigationInfos.get(id);

        this._navigationInfos.delete(id);

        if (!navigationInfo) {
            return;
        }

        navigationInfo.urlAfterRedirects = urlAfterRedirects;

        const currentNavigation = this._router.getCurrentNavigation();

        if (!currentNavigation) {
            return this._sendBasicEventTrackerNavigationEvent(eventStatus, navigationInfo);
        }

        return this._sendFullEventTrackerNavigationEvent(eventStatus, navigationInfo, currentNavigation);
    }

    private _sendBasicEventTrackerNavigationEvent(eventStatus: SuccessOrErrorEventStatus, navigationInfo: NavigationInfo): void {
        const additionalData: AdditionalNavigationEventData = {
            from: navigationInfo.from,
            toRequested: navigationInfo.url,
            toResolved: navigationInfo.urlAfterRedirects,

            trigger: navigationInfo.trigger,
            navigatedBy: undefined,
        };

        return this._sendEventTrackerNavigationEvent(eventStatus, additionalData, navigationInfo.traceId);
    }

    private _sendFullEventTrackerNavigationEvent(
        eventStatus: SuccessOrErrorEventStatus,
        navigationInfo: NavigationInfo,
        currentNavigation: Navigation,
    ): void {
        const { finalUrl, initialUrl, extras, trigger } = currentNavigation;

        const additionalData: AdditionalNavigationEventData = {
            from: navigationInfo.from,
            toRequested: initialUrl.toString() ?? navigationInfo.url,
            toResolved: finalUrl?.toString() ?? navigationInfo.urlAfterRedirects,

            trigger: trigger ?? navigationInfo.trigger,
            navigatedBy: extras?.state?.navigatedBy || (trigger === 'popstate' ? 'history' : undefined),
        };

        return this._sendEventTrackerNavigationEvent(eventStatus, additionalData, navigationInfo.traceId);
    }

    private _sendEventTrackerNavigationEvent(
        eventStatus: EventTrackerEventStatus,
        additionalData: AdditionalNavigationEventData,
        traceId: string,
    ): void {
        const eventTrackerEvent: EventTrackerEventDto = {
            event: EventTrackerEvents.Navigation,
            eventStatus,
            additionalData: [additionalData],
        };

        return this._eventTrackerService.fireAndForgetEvent(eventTrackerEvent, traceId);
    }
}
